import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack, LibraryAdd } from "@mui/icons-material";

import BackButton from "../common/BackButton";
import { useDebounce } from "../../hooks/useDebounce";
import CampaignCard from "./common/CampaignCard";
import CampaignFilters, { MONTH_LABELS, countNetRevenue, matchesNetRevenueFilter } from "./common/CampaignFilters";
import CampaignSort, { DEFAULT_SORT } from "./common/CampaignSort";
import GroupCard from "./common/GroupCard";
import RevenueMonthFilter from "./common/RevenueMonthFilter";
import { computeGroupTotals, getCategoryLabel, uniqueValues } from "./common/campaignFieldUtils";

// Ties a sort field to the property it actually reads, and whether that
// property is a number (compared numerically) or not (compared as a
// case-insensitive string) — one lookup table instead of a branch per field
// inside the comparator below. Separate maps for campaigns vs. groups since
// a group has no single totalPayment/paymentLimit of its own (see
// computeGroupTotals) and no revenue concept at all.
const CAMPAIGN_SORT_ACCESSORS = {
  name: { get: (c) => c.name || '', numeric: false },
  budget: { get: (c) => Number(c.totalPayment) || 0, numeric: true },
  limit: { get: (c) => Number(c.paymentLimit) || 0, numeric: true },
  revenue: { get: (c) => Number(c.netRevenue) || 0, numeric: true },
};

const GROUP_SORT_ACCESSORS = {
  name: { get: (g) => g.name || '', numeric: false },
  budget: { get: (g) => computeGroupTotals(g).totalBudget, numeric: true },
  limit: { get: (g) => computeGroupTotals(g).totalLimit, numeric: true },
};

// Client-side sort for datasets that are already fully loaded in one shot
// (the Groups list, or a revenue month's fixed result set) — never used
// against the plain browse/search campaign list, which is either paginated
// (and so must be sorted server-side, see CampaignsDashboard's handleChange)
// or, once /search takes over, already arrives pre-sorted from there.
// Unrecognized/inapplicable fields (e.g. "revenue" against GROUP_SORT_ACCESSORS,
// which has no such key) leave the list untouched rather than erroring.
function applySort(list, sort, accessors) {
  const accessor = accessors[sort.field];
  if (!accessor) return list;
  const direction = sort.order === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    const av = accessor.get(a);
    const bv = accessor.get(b);
    if (accessor.numeric) return (av - bv) * direction;
    return av.localeCompare(bv, undefined, { sensitivity: 'base' }) * direction;
  });
}

const DEFAULT_FILTERS = {
  fy: 'All',
  month: 'All',
  category: [],
  platform: [],
  createdby: [],
  minBudget: '',
  maxBudget: '',
  minLimit: '',
  maxLimit: '',
  netRevenue: 'All',
};

const VIEW_MODES = { ALL: 'all', GROUPS: 'groups' };

// Survives this component's own mount/unmount (e.g. navigating from a
// campaign card to its Details page and back — a different route, so this
// whole component unmounts and would otherwise reset every local useState
// below to its default) so a visitor doesn't have to redo their
// search/filters/sort on every return trip. Deliberately plain module state
// rather than sessionStorage: it resets on a full page reload, which is fine
// here, and needs no serialization for the platform/createdby arrays inside
// `filters`.
let cachedListState = { search: '', filters: DEFAULT_FILTERS, sort: DEFAULT_SORT };

const Home = React.memo(function Home({
  loading,
  campaigns,
  handleLinkGenerate,
  handleChange,
  editCampaign,
  handleDelete,
  totalCampaigns,
  isFiltering,
  filterOptions,
  groups: groupsProp,
  viewingGroup,
  onSelectGroup,
  onExitGroupView,
  selectionMode,
  onToggleSelectionMode,
  selectedIds,
  onToggleSelect,
  onOpenGroupDialog,
  onRemoveFromGroup,
  isSuperUser = false,
  onAddRevenue,
  onEditBudgetLimit,
  onFreezeToggle,
  onShare,
  revenueSummary,
  revenueSummaryLoading,
  onLoadRevenueSummary,
  onClearRevenueSummary,
}) {
  // A groups fetch that's still in flight, or that genuinely fails, should
  // never crash this page's render — every place below expects a real array.
  // Memoized so the [] fallback is a stable reference, not a new array (and
  // therefore a spurious dependency-change) on every render.
  const groups = useMemo(() => (Array.isArray(groupsProp) ? groupsProp : []), [groupsProp]);

  const [viewMode, setViewMode] = useState(VIEW_MODES.ALL);
  const [search, setSearch] = useState(cachedListState.search);
  const [filters, setFilters] = useState(cachedListState.filters);
  const [sort, setSort] = useState(cachedListState.sort);
  const [groupSearch, setGroupSearch] = useState('');

  // Keeps the module-level cache above current so the *next* mount (a
  // return trip from Details) picks up wherever this one left off.
  useEffect(() => {
    cachedListState = { search, filters, sort };
  }, [search, filters, sort]);

  // Filtering a group's own campaigns is a pure client-side operation —
  // the drill-down view already has the group's full (small) member list
  // loaded, so there's no reason to round-trip to /campaign/search the way
  // the main list does. Financial Year and Month are intentionally left out
  // here: a group is a fixed, hand-picked bundle rather than a time-scoped
  // set, so neither filter maps cleanly onto it.
  const [groupCampaignSearch, setGroupCampaignSearch] = useState('');
  const [groupCampaignFilters, setGroupCampaignFilters] = useState(DEFAULT_FILTERS);

  // Switching to a different group (or leaving one) resets any filter left
  // over from the last group — otherwise "no campaigns match" could greet
  // you in a brand new group for no visible reason.
  useEffect(() => {
    setGroupCampaignSearch('');
    setGroupCampaignFilters(DEFAULT_FILTERS);
  }, [viewingGroup?._id]);

  // Search text and every filter control (including quick successive
  // Autocomplete picks) settle through one shared debounce, so selecting
  // three categories in a row fires a single request instead of three
  // racing ones.
  const pending = useMemo(() => ({ search, filters }), [search, filters]);
  const debouncedPending = useDebounce(pending, 350);

  // Name/Budget/Limit apply to the plain browse list too, which is paginated
  // (see CampaignsDashboard) — so, unlike search text and the other filters,
  // they can't just be sorted client-side over whatever page has loaded so
  // far and must round-trip to the server instead. Deliberately NOT
  // debounced with the rest of `pending` below: picking a sort option is one
  // deliberate click, not something that benefits from waiting out a typing
  // pause, and should feel immediate. Revenue is excluded — /search has no
  // notion of it; it only ever applies client-side, to the already-loaded
  // revenue-month result set (see isRevenueViewActive below).
  const sortParams = useMemo(
    () =>
      sort.field === 'name' || sort.field === 'budget' || sort.field === 'limit'
        ? { sortBy: sort.field, sortOrder: sort.order }
        : {},
    [sort]
  );

  const effectiveFilters = useMemo(
    () => ({
      fy: debouncedPending.filters.fy,
      month: debouncedPending.filters.month,
      category: debouncedPending.filters.category,
      platform: debouncedPending.filters.platform.map((p) => p.id),
      createdby: debouncedPending.filters.createdby.map((c) => c.id),
      minBudget: debouncedPending.filters.minBudget,
      maxBudget: debouncedPending.filters.maxBudget,
      minLimit: debouncedPending.filters.minLimit,
      maxLimit: debouncedPending.filters.maxLimit,
      netRevenue: debouncedPending.filters.netRevenue,
      ...sortParams,
    }),
    [debouncedPending, sortParams]
  );

  useEffect(() => {
    // A group's membership is a fixed set — the free-text/filter bar is
    // hidden while viewing one, so it must not also drive a /search fetch
    // that would immediately overwrite the group's campaign list. The
    // dedicated Groups tab is likewise a pure client-side view over
    // already-loaded groups, so it doesn't drive a campaign fetch either.
    if (viewingGroup || viewMode === VIEW_MODES.GROUPS) return;
    handleChange(debouncedPending.search.trim(), effectiveFilters);
  }, [handleChange, debouncedPending, effectiveFilters, viewingGroup, viewMode]);

  // A revenue-month selection only makes sense on the flat "All Campaigns"
  // list — leaving it active while drilling into a group or the Groups tab
  // would superimpose a stale total/list over an unrelated view, so it's
  // dropped the moment either happens.
  useEffect(() => {
    if (revenueSummary && (viewingGroup || viewMode === VIEW_MODES.GROUPS)) {
      onClearRevenueSummary?.();
    }
  }, [viewingGroup, viewMode, revenueSummary, onClearRevenueSummary]);

  // Revenue is only ever meaningful while a revenue month is actually
  // loaded (CampaignSort disables picking it otherwise) — but if one WAS
  // active and gets cleared (or a group/the Groups tab clears it via the
  // effect above), a lingering "Revenue" selection would leave the Sort
  // button visibly claiming a sort that's silently no longer being applied
  // to anything.
  useEffect(() => {
    if (sort.field === 'revenue' && !revenueSummary) {
      setSort(DEFAULT_SORT);
    }
  }, [revenueSummary, sort.field]);

  // Backend always returns { totalNetRevenue: number, campaigns: array }, but
  // this guards against a malformed/future-changed response crashing the
  // whole page's render instead of just failing to show a number.
  const safeRevenueSummary = useMemo(() => {
    if (!revenueSummary) return null;
    return {
      ...revenueSummary,
      campaigns: Array.isArray(revenueSummary.campaigns) ? revenueSummary.campaigns : [],
      totalNetRevenue: Number.isFinite(revenueSummary.totalNetRevenue) ? revenueSummary.totalNetRevenue : 0,
    };
  }, [revenueSummary]);

  const isRevenueViewActive = Boolean(safeRevenueSummary) && !viewingGroup && viewMode !== VIEW_MODES.GROUPS;
  const isNetRevenueFilterActive = isFiltering && Boolean(debouncedPending.filters.netRevenue) &&
    debouncedPending.filters.netRevenue !== 'All';

  // On the "All Campaigns" tab, grouped campaigns collapse into a single
  // GroupCard instead of appearing as individual cards. In plain browse
  // mode, `campaigns` is only whichever page(s) infinite-scroll has loaded
  // so far — gating a group's visibility on that would make it flicker in
  // and out as more pages load (or load in the background while a
  // different tab is open) purely by pagination timing, which is exactly
  // what was happening here. So: show every group unconditionally while
  // browsing, and only narrow to "groups with a member in this result set"
  // once isFiltering is true, because then `campaigns` is a real, complete
  // server-side result set rather than a partial page.
  const { visibleGroups, ungroupedCampaigns } = useMemo(() => {
    // A super-admin's month/year revenue selection overrides the normal
    // browse/search/group list entirely — it's a fixed, server-computed set
    // scoped to that month, not something groups or the search box apply to.
    // Only takes effect on the flat "All Campaigns" list (see
    // isRevenueViewActive) — a group view or the Groups tab wins instead.
    // Sorted client-side (including by Revenue) since this is already the
    // complete result set for the month, unlike the plain browse list below.
    if (isRevenueViewActive) {
      return { visibleGroups: [], ungroupedCampaigns: applySort(safeRevenueSummary.campaigns, sort, CAMPAIGN_SORT_ACCESSORS) };
    }

    // A GroupCard's Net Revenue is the total across ALL of the group's
    // members, so collapsing a matching campaign into its group would show
    // e.g. a positive group total under a "Loss" filter. While a net revenue
    // filter is active, each matching campaign is shown on its own instead.
    // Uses the debounced value so the view switches together with the
    // results it applies to.
    if (viewingGroup || isNetRevenueFilterActive) {
      return { visibleGroups: [], ungroupedCampaigns: campaigns };
    }

    let relevantGroups = groups;
    if (isFiltering) {
      const idsInView = new Set(campaigns.map((c) => c._id));
      relevantGroups = groups.filter((g) => (g.campaigns || []).some((c) => idsInView.has(c._id || c)));
    }

    const groupedIds = new Set(
      relevantGroups.flatMap((g) => (g.campaigns || []).map((c) => c._id || c))
    );

    return {
      // Groups are always fully loaded (never paginated), so sorting them is
      // safe client-side regardless of Name/Budget/Limit — but `campaigns`
      // itself is NOT re-sorted here: it's either the plain cursor-paginated
      // browse list (sort.field === 'default', nothing to apply) or the
      // complete /search result, which the backend already returned in the
      // requested order (see CampaignsDashboard's handleChange/sortBy).
      // .filter() below preserves that order either way.
      visibleGroups: applySort(relevantGroups, sort, GROUP_SORT_ACCESSORS),
      ungroupedCampaigns: campaigns.filter((c) => !groupedIds.has(c._id)),
    };
  }, [campaigns, groups, viewingGroup, isFiltering, isRevenueViewActive, isNetRevenueFilterActive, safeRevenueSummary, sort]);

  const visibleCount = visibleGroups.length + ungroupedCampaigns.length;

  // Facet options for the group-local filter bar, derived straight from
  // the group's own members rather than the server — every option offered
  // is guaranteed to match at least one campaign in this specific group.
  const groupFilterOptions = useMemo(() => {
    if (!viewingGroup) return null;

    const platformMap = new Map();
    const creatorMap = new Map();
    let minBudget = Infinity;
    let maxBudget = 0;
    let minLimit = Infinity;
    let maxLimit = 0;

    campaigns.forEach((c) => {
      (c.platform || []).forEach((p) => {
        if (p?._id) platformMap.set(String(p._id), p.name);
      });
      const creator = c.createdby;
      if (creator?._id) creatorMap.set(String(creator._id), creator.fullName);
      const budget = Number(c.totalPayment) || 0;
      minBudget = Math.min(minBudget, budget);
      maxBudget = Math.max(maxBudget, budget);
      const limit = Number(c.paymentLimit) || 0;
      minLimit = Math.min(minLimit, limit);
      maxLimit = Math.max(maxLimit, limit);
    });

    return {
      categories: uniqueValues(campaigns.map(getCategoryLabel)),
      platforms: Array.from(platformMap, ([id, name]) => ({ id, name })),
      creators: Array.from(creatorMap, ([id, name]) => ({ id, name })),
      budgetRange: { min: campaigns.length ? minBudget : 0, max: maxBudget },
      limitRange: { min: campaigns.length ? minLimit : 0, max: maxLimit },
      netRevenueCounts: countNetRevenue(campaigns),
    };
  }, [viewingGroup, campaigns]);

  const groupCampaignPending = useDebounce(
    { search: groupCampaignSearch, filters: groupCampaignFilters },
    250
  );

  const filteredGroupCampaigns = useMemo(() => {
    if (!viewingGroup) return [];

    const term = groupCampaignPending.search.trim().toLowerCase();
    const { category, platform, createdby, minBudget, maxBudget, minLimit, maxLimit, netRevenue } = groupCampaignPending.filters;
    const platformIds = new Set(platform.map((p) => p.id));
    const creatorIds = new Set(createdby.map((c) => c.id));
    const min = minBudget ? Number(minBudget) : null;
    const max = maxBudget ? Number(maxBudget) : null;
    const minL = minLimit ? Number(minLimit) : null;
    const maxL = maxLimit ? Number(maxLimit) : null;

    return campaigns.filter((c) => {
      if (term && !c.name?.toLowerCase().includes(term)) return false;
      if (category.length && !category.includes(getCategoryLabel(c))) return false;
      if (platformIds.size && !(c.platform || []).some((p) => platformIds.has(String(p?._id)))) return false;
      if (creatorIds.size && !creatorIds.has(String(c.createdby?._id))) return false;
      const budget = Number(c.totalPayment) || 0;
      if (min != null && budget < min) return false;
      if (max != null && budget > max) return false;
      const limit = Number(c.paymentLimit) || 0;
      if (minL != null && limit < minL) return false;
      if (maxL != null && limit > maxL) return false;
      if (!matchesNetRevenueFilter(c, netRevenue)) return false;
      return true;
    });
  }, [viewingGroup, campaigns, groupCampaignPending]);

  const isGroupFiltering =
    Boolean(groupCampaignPending.search.trim()) ||
    groupCampaignPending.filters.category.length > 0 ||
    groupCampaignPending.filters.platform.length > 0 ||
    groupCampaignPending.filters.createdby.length > 0 ||
    Boolean(groupCampaignPending.filters.minBudget) ||
    Boolean(groupCampaignPending.filters.maxBudget) ||
    Boolean(groupCampaignPending.filters.minLimit) ||
    Boolean(groupCampaignPending.filters.maxLimit) ||
    groupCampaignPending.filters.netRevenue !== 'All';

  const matchingGroups = useMemo(() => {
    const term = groupSearch.trim().toLowerCase();
    const matches = term ? groups.filter((g) => g.name.toLowerCase().includes(term)) : groups;
    return applySort(matches, sort, GROUP_SORT_ACCESSORS);
  }, [groups, groupSearch, sort]);

  const headerLabel = viewingGroup
    ? `${viewingGroup.name} (${filteredGroupCampaigns.length}${
        isGroupFiltering ? ` of ${campaigns.length}` : ''
      })`
    : viewMode === VIEW_MODES.GROUPS
    ? `Groups (${groups.length})`
    : isRevenueViewActive
    ? `Campaigns (${safeRevenueSummary.campaigns.length} in ${
        MONTH_LABELS[safeRevenueSummary.month] || safeRevenueSummary.month
      } ${safeRevenueSummary.year})`
    : isFiltering
    ? // The server's exact total, not visibleCount — that counts cards on
      // screen, so it undercounts both grouped campaigns (collapsed into one
      // GroupCard) and any result page not yet scrolled into view.
      `Campaigns (${totalCampaigns ?? visibleCount} matching)`
    : `Campaigns (${totalCampaigns || 'Loading...'})`;

  // Only re-filtering an already-populated list gets the dim + progress-bar
  // treatment — plain infinite-scroll page loads append silently so
  // scrolling doesn't flicker on every page fetch.
  const isRefreshingFilteredView = loading && isFiltering && campaigns.length > 0;
  const isInitialLoading = loading && campaigns.length === 0;
  // Guarded by isFiltering/totalCampaigns (rather than just "!loading, no
  // campaigns") so this doesn't flash on the very first paint, before the
  // initial browse-mode fetch has even had a chance to start.
  const isEmptyResult = !loading && visibleCount === 0 && (isFiltering || totalCampaigns === 0);

  const selectedCount = selectedIds.size;

  return (
    <div>
      <Box
        width={{ xs: "100%", lg: `calc(100% - 80px)` }}
        marginLeft={{ xs: "0px", lg: `80px` }}
        marginTop={8}
        paddingTop={1}
        backgroundColor={"#F0F2F5"}
        minHeight={"100vh"}
        height={"100%"}
      >
        <BackButton label={headerLabel} />
        <Box sx={{ padding: 2, paddingBottom: selectionMode ? 10 : 2 }}>
          <Stack spacing={2}>
            {!viewingGroup && (
              <Tabs
                value={viewMode}
                onChange={(e, value) => setViewMode(value)}
                sx={{
                  minHeight: 40,
                  "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 600 },
                }}
              >
                <Tab label="All Campaigns" value={VIEW_MODES.ALL} />
                <Tab
                  value={VIEW_MODES.GROUPS}
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>Groups</span>
                      <Chip
                        label={groups.length}
                        size="small"
                        color={viewMode === VIEW_MODES.GROUPS ? "primary" : "default"}
                        sx={{ height: 18, fontSize: 11, fontWeight: 700 }}
                      />
                    </Stack>
                  }
                />
              </Tabs>
            )}

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                borderColor: "rgba(15, 23, 42, 0.08)",
                backgroundColor: "#fff",
              }}
            >
              {viewingGroup ? (
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
                    <Button
                      size="small"
                      startIcon={<ArrowBack fontSize="small" />}
                      onClick={onExitGroupView}
                    >
                      All Campaigns
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      Viewing group “{viewingGroup.name}”
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={2}
                    flexWrap="wrap"
                    rowGap={1.5}
                    alignItems="flex-start"
                    justifyContent="center"
                  >
                    <TextField
                      sx={{ width: 400, maxWidth: "100%" }}
                      label="Search in this group..."
                      type="search"
                      size="small"
                      value={groupCampaignSearch}
                      onChange={(e) => setGroupCampaignSearch(e.target.value)}
                    />
                    {groupFilterOptions && (
                      <CampaignFilters
                        filters={groupCampaignFilters}
                        onFiltersChange={setGroupCampaignFilters}
                        filterOptions={groupFilterOptions}
                        hideFy
                        hideMonth
                        showNetRevenue={isSuperUser}
                      />
                    )}
                  </Stack>
                </Stack>
              ) : viewMode === VIEW_MODES.GROUPS ? (
                <Stack direction="row" spacing={2} justifyContent="center" alignItems="flex-start">
                  <TextField
                    sx={{ width: 400, maxWidth: "100%" }}
                    label="Search groups..."
                    type="search"
                    size="small"
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                  />
                  <CampaignSort sort={sort} onChange={setSort} revenueAvailable={false} />
                </Stack>
              ) : (
                <Stack
                  direction="row"
                  spacing={2}
                  flexWrap="wrap"
                  rowGap={1.5}
                  alignItems="flex-start"
                  justifyContent="center"
                >
                  <TextField
                    sx={{ width: 400, maxWidth: "100%" }}
                    id="outlined-search"
                    label="Search campaign..."
                    type="search"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <CampaignFilters
                    filters={filters}
                    onFiltersChange={setFilters}
                    filterOptions={filterOptions}
                    showNetRevenue={isSuperUser}
                  />
                  <CampaignSort sort={sort} onChange={setSort} revenueAvailable={isSuperUser && isRevenueViewActive} />
                  {isSuperUser && (
                    <RevenueMonthFilter
                      value={
                        safeRevenueSummary
                          ? { month: safeRevenueSummary.month, year: safeRevenueSummary.year }
                          : null
                      }
                      onApply={onLoadRevenueSummary}
                      onClear={onClearRevenueSummary}
                    />
                  )}
                  <Button
                    size="small"
                    variant={selectionMode ? 'contained' : 'outlined'}
                    color={selectionMode ? 'error' : 'primary'}
                    startIcon={<LibraryAdd fontSize="small" />}
                    onClick={onToggleSelectionMode}
                  >
                    {selectionMode ? 'Cancel Selection' : 'Select to Group'}
                  </Button>
                </Stack>
              )}
            </Paper>

            {isSuperUser && isRevenueViewActive && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 3,
                  borderColor: 'rgba(124, 58, 237, 0.3)',
                  backgroundColor: 'rgba(124, 58, 237, 0.06)',
                  textAlign: 'center',
                }}
              >
                {revenueSummaryLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#7C3AED' }}>
                    Net Revenue — {MONTH_LABELS[safeRevenueSummary.month] || safeRevenueSummary.month}{' '}
                    {safeRevenueSummary.year}: ₹{safeRevenueSummary.totalNetRevenue.toLocaleString('en-IN')}
                    {' '}
                    <Typography component="span" variant="body2" color="text.secondary">
                      ({safeRevenueSummary.campaigns.length} campaign
                      {safeRevenueSummary.campaigns.length === 1 ? '' : 's'})
                    </Typography>
                  </Typography>
                )}
              </Paper>
            )}

            {!viewingGroup && viewMode === VIEW_MODES.GROUPS ? (
              matchingGroups.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <Typography variant="body1" color="text.secondary">
                    {groups.length === 0
                      ? "No groups yet — select a few campaigns and group them together."
                      : "No groups match your search."}
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {matchingGroups.map((group) => (
                    <Grid item xs={12} sm={6} lg={4} xl={3} key={group._id}>
                      <GroupCard group={group} onOpen={onSelectGroup} disabled={selectionMode} isSuperUser={isSuperUser} />
                    </Grid>
                  ))}
                </Grid>
              )
            ) : (
              <>
                <Box sx={{ height: 4 }}>
                  {isRefreshingFilteredView && <LinearProgress />}
                </Box>

                {viewingGroup && !isInitialLoading && filteredGroupCampaigns.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <Typography variant="body1" color="text.secondary">
                      {campaigns.length === 0
                        ? "This group has no campaigns."
                        : "No campaigns in this group match your filters."}
                    </Typography>
                  </Box>
                ) : !viewingGroup && isEmptyResult ? (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <Typography variant="body1" color="text.secondary">
                      No campaigns match your filters.
                    </Typography>
                  </Box>
                ) : isInitialLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      opacity: isRefreshingFilteredView ? 0.5 : 1,
                      pointerEvents: isRefreshingFilteredView ? "none" : "auto",
                      transition: "opacity 0.15s ease",
                    }}
                  >
                    <Grid container spacing={2}>
                      {!viewingGroup &&
                        visibleGroups.map((group) => (
                          <Grid item xs={12} sm={6} lg={4} xl={3} key={group._id}>
                            <GroupCard group={group} onOpen={onSelectGroup} disabled={selectionMode} isSuperUser={isSuperUser} />
                          </Grid>
                        ))}
                      {(viewingGroup ? filteredGroupCampaigns : ungroupedCampaigns).map((campaign) => (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          lg={4}
                          xl={3}
                          key={campaign._id}
                        >
                          <CampaignCard
                            campaignDetails={campaign}
                            handleLinkGenerate={handleLinkGenerate}
                            selectable={selectionMode}
                            selected={selectedIds.has(campaign._id)}
                            onToggleSelect={onToggleSelect}
                            onRemoveFromGroup={viewingGroup ? onRemoveFromGroup : undefined}
                            isSuperUser={isSuperUser}
                            onAddRevenue={onAddRevenue}
                            onEditBudgetLimit={onEditBudgetLimit}
                            onFreezeToggle={onFreezeToggle}
                            onShare={onShare}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </>
            )}
          </Stack>
        </Box>
      </Box>

      {selectionMode && (
        <Paper
          elevation={6}
          sx={{
            position: "fixed",
            bottom: 0,
            left: { xs: 0, lg: "80px" },
            right: 0,
            zIndex: 1200,
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {selectedCount} campaign{selectedCount === 1 ? '' : 's'} selected
          </Typography>
          <Button
            variant="contained"
            startIcon={<LibraryAdd />}
            disabled={selectedCount === 0}
            onClick={onOpenGroupDialog}
          >
            Group Selected
          </Button>
          <Button color="secondary" onClick={onToggleSelectionMode}>
            Cancel
          </Button>
        </Paper>
      )}
    </div>
  );
});

export default Home;
